"use client";

import styles from "@/styles/category/index.module.scss";
import Image from "next/image";
import { useState, useEffect } from "react";
import searchApi from "@/service/modules/searchApi";
import { BreadCrumbItem, ProductType } from "@/types/product";
import dayjs from "dayjs";
import ProductItem from "@/components/global/ProductItem";
import ProductCardFullWidth from "@/components/global/ProductCardFullWidth";
import FilterBox from "@/components/category/FilterBox";
import { List, Loading } from "react-vant";
import { setGtmDataLayer } from "@/utils/gtmDataLayer";
import { matomoSetCustomDimension } from "@/utils/matomoEvent";
import { getCookie } from "@/utils/cookie";

// 获取分类列表数据 - 使用React cache避免重复请求
const getProductList = async (id: string, params: any) => {
  try {
    // 清空params中的空值键值对
    const dataObj = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== null && value !== ""
      )
    );

    const productData = await searchApi.getSchemeList(id, dataObj);

    if (
      productData.data &&
      productData.data.pageList &&
      productData.data.pageList.items &&
      productData.data.pageList.items.length
    ) {
      productData.data.pageList.items.forEach((item: any) => {
        item.imgUrl = item.filename || item.newThumbnail;
      });
    }

    // 对 sidebarFilter 进行排序，将 key 为 "category" 的元素排到最前面
    if (
      productData.data &&
      productData.data.sidebarFilter &&
      Array.isArray(productData.data.sidebarFilter)
    ) {
      productData.data.sidebarFilter.sort((a: any, b: any) => {
        if (a.key === "category") return -1;
        if (b.key === "category") return 1;
        return 0;
      });
    }

    return {
      ...productData.data,
    };
  } catch (error) {
    console.log(
      "Next.js Error :",
      dayjs().format("YY-MM-DD HH:mm:ss"),
      `  Page Shop: Data acquisition failed:`,
      JSON.stringify(error)
    );
  }
};

interface PageListType {
  items: ProductType[];
  total: number;
  page: number;
  perPage: number;
}

interface ShopContentProps {
  schemeId: string;
  breadCrumb: BreadCrumbItem[];
  pageList: PageListType;
  categoryNameFilter: string;
  sidebarFilter: any[];
}

export default function ShopContent({
  schemeId,
  breadCrumb,
  pageList,
  categoryNameFilter,
  sidebarFilter,
}: ShopContentProps) {
  // 将传入的 sidebarFilter 存储到本地状态，便于页面重新请求时保持和重新赋值
  const [localSidebarFilter, setLocalSidebarFilter] = useState<any[]>(
    sidebarFilter ? sidebarFilter : []
  );
  // 维护请求参数对象（用于 getProductList）
  const [queryParams, setQueryParams] = useState<any>({
    category: categoryNameFilter,
    page: pageList?.page ? pageList.page : 1,
    perPage: 16,
  });
  // 产品列表数据
  const [productList, setProductList] = useState<ProductType[]>(
    pageList?.items || []
  );
  // 是否已加载完所有数据
  const [finished, setFinished] = useState<boolean>(false);
  // 当前页码
  const [currentPage, setCurrentPage] = useState<number>(pageList?.page || 1);
  // 总数据量
  const [total, setTotal] = useState<number>(pageList?.total || 0);
  // 筛选/排序时的加载状态
  const [isRefreshing, setIsRefreshing] = useState(false);
  // 添加展示模式状态，默认为网格模式（一行两个商品）
  const [isGridMode, setIsGridMode] = useState(true);

  useEffect(() => {
    const g_params = {
      event: "productListPage",
      remarketing_params: {
        ecomm_prodid: "",
        ecomm_pagetype: "category",
        ecomm_totalvalue: "",
      },
      params: { userId: null, categoryId: null },
      emarketing_params: {
        pagetype: "",
        productid: "",
        productsku: "",
        searchterm: "",
        category: breadCrumb[0] ? breadCrumb[0].name : "",
        cartinfo: "",
        orderid: "",
        customerid: getCookie("userCustomerId") || null,
        customeremail: localStorage.getItem("userEmail") || null,
        orderinfo: "",
      },
    };

    setGtmDataLayer(g_params);

    const url = breadCrumb.length ? breadCrumb[breadCrumb.length - 1].url : "";
    if (url) {
      matomoSetCustomDimension(28, url);
    }
  }, []);

  // 当父组件传入的 sidebarFilter 变化时，同步更新本地状态
  useEffect(() => {
    let sidebarFilterFormatter = sidebarFilter ? sidebarFilter : [];

    // 只保留key为"category"、"price-range"和"hot-brand"的元素
    sidebarFilterFormatter = sidebarFilterFormatter.filter(
      (item) =>
        item.key === "category" ||
        item.key === "price-range" ||
        item.key === "hot-brand"
    );

    // 插入一个新的元素
    sidebarFilterFormatter.push({
      key: "popularity",
      title: "Popularity",
    });

    setLocalSidebarFilter(sidebarFilterFormatter);
  }, [sidebarFilter]);

  // 当上层 props 发生变化时，同步初始化本地参数与数据
  useEffect(() => {
    setQueryParams((prev: any) => ({
      ...prev,
      category: categoryNameFilter,
      page: pageList?.page ? pageList.page : 1,
      perPage: 16,
    }));
    setProductList(pageList?.items || []);
    setTotal(pageList?.total || 0);
    setCurrentPage(pageList?.page || 1);

    // 检查是否已加载完所有数据
    if (pageList?.items && pageList?.total) {
      setFinished(pageList.items.length >= pageList.total);
    }
  }, [categoryNameFilter, pageList]);

  // 滚动加载更多数据
  const onLoad = async () => {
    if (finished) return;

    try {
      const nextPage = currentPage + 1;
      const nextParams = {
        ...queryParams,
        page: nextPage,
      };

      if (!nextParams.sortBy) {
        nextParams.sortBy = "";
        nextParams.sortDirection = "";
      }

      const data = await getProductList(schemeId, nextParams);

      if (
        data &&
        data.pageList &&
        data.pageList.items &&
        data.pageList.items.length > 0
      ) {
        setProductList((prev) => [...prev, ...data.pageList.items]);
        setCurrentPage(nextPage);
        setTotal(data.pageList.total);

        // 更新 sidebarFilter（如果新数据中有）
        if (data.sidebarFilter && Array.isArray(data.sidebarFilter)) {
          let sidebarFilterFormatter = [...data.sidebarFilter];
          sidebarFilterFormatter = sidebarFilterFormatter.filter(
            (item) =>
              item.key === "category" ||
              item.key === "price-range" ||
              item.key === "hot-brand"
          );
          sidebarFilterFormatter.push({
            key: "popularity",
            title: "Popularity",
          });
          setLocalSidebarFilter(sidebarFilterFormatter);
        }

        // 检查是否已加载完所有数据
        const loadedCount = productList.length + data.pageList.items.length;
        if (loadedCount >= data.pageList.total) {
          setFinished(true);
        }
      } else {
        setFinished(true);
      }
    } catch (error) {
      console.error("加载更多失败:", error);
      setFinished(true);
    }
  };

  // 根据参数刷新列表（用于筛选/排序）
  const refreshList = async (nextParams: any) => {
    setIsRefreshing(true);

    try {
      const data = await getProductList(schemeId, nextParams);
      if (data && data.pageList) {
        setProductList(data.pageList.items || []);
        setTotal(data.pageList.total || 0);
        setCurrentPage(nextParams.page || 1);
        setFinished(
          (data.pageList.items?.length || 0) >= (data.pageList.total || 0)
        );

        // 更新 sidebarFilter（如果新数据中有）
        if (data.sidebarFilter && Array.isArray(data.sidebarFilter)) {
          let sidebarFilterFormatter = [...data.sidebarFilter];
          sidebarFilterFormatter = sidebarFilterFormatter.filter(
            (item) =>
              item.key === "category" ||
              item.key === "price-range" ||
              item.key === "hot-brand"
          );
          sidebarFilterFormatter.push({
            key: "popularity",
            title: "Popularity",
          });
          setLocalSidebarFilter(sidebarFilterFormatter);
        }
      } else {
        setProductList([]);
        setTotal(0);
        setFinished(true);
      }
    } catch (error) {
      console.error("刷新列表失败:", error);
      setProductList([]);
      setTotal(0);
      setFinished(true);
    } finally {
      setIsRefreshing(false);
    }
  };

  // 切换展示模式
  const toggleDisplayMode = () => {
    setIsGridMode(!isGridMode);
  };

  // 处理筛选变化的回调函数（单选）：任何筛选或排序都会覆盖之前相同类型的值
  const handleSortChange = (option: {
    type: string;
    value?: string;
    item?: any;
  }) => {
    console.log("option", option);
    // 直接基于当前的 queryParams 构建新参数
    const nextParams: any = { ...queryParams };
    // 重置参数
    delete nextParams.minPrice;
    delete nextParams.maxPrice;
    delete nextParams.brand;
    delete nextParams.categoryID;
    delete nextParams.sortBy;
    delete nextParams.sortDirection;
    delete nextParams.freeShipping; // 清除免邮筛选
    switch (option.type) {
      case "sort":
        // 排序这边需要再处理一次
        switch (option.value) {
          case "price-low-to-high":
            nextParams.sortBy = "price";
            nextParams.sortDirection = "asc";
            break;
          case "price-high-to-low":
            nextParams.sortBy = "price";
            nextParams.sortDirection = "desc";
            break;
        }
        break;
      case "filter":
        if (option.value == "newArrival") {
          nextParams.sortBy = "addTime";
        } else if (option.value == "freeshipping") {
          nextParams.freeShipping = 1;
        }
        break;
      case "price-range":
        nextParams.minPrice = option.item.minPrice;
        nextParams.maxPrice = option.item.maxPrice;
        break;
      case "hot-brand":
        nextParams.brand = option.item.name || "";
        break;
      case "category":
        nextParams.categoryID = option.item.categoryID || "";
        break;
    }

    // 更新状态并刷新列表
    setQueryParams(nextParams);
    refreshList(nextParams);
  };

  return (
    <div className={styles["comp-category-content"]}>
      <div className={styles["content-title"]}>
        <div className={styles["category-name"]}>
          <span className={styles["category-name-text"]}>
            {breadCrumb &&
              breadCrumb.length > 0 &&
              (() => {
                const currentItem = breadCrumb.find((item) => item.current);
                if (currentItem && currentItem.name) {
                  return currentItem.name;
                }
                return breadCrumb[breadCrumb.length - 1].name;
              })()}
          </span>
          {/* 展示数量 */}
          {total > 0 && <span className={styles["category-count"]}>&nbsp;({total})</span>}
        </div>
        <Image
          src={`${process.env.IMAGE_DOMAIN}/svg/category-page/${
            isGridMode ? "toggle-column.svg" : "toggle-grid.svg"
          }`}
          alt="Grid"
          className={styles["display-toggle"]}
          onClick={toggleDisplayMode}
          width={20}
          height={20}
        />
      </div>

      <FilterBox
        onSortChange={handleSortChange}
        sidebarFilter={localSidebarFilter}
        categoryNameFilter={categoryNameFilter}
      />

      {/* 产品列表 */}
      <div className={styles["product-list-container"]}>
        {/* 加载蒙层 */}
        {isRefreshing && (
          <div className={styles["loading-overlay"]}>
            <Loading color="#D90B17" style={{ marginTop: "100px" }} />
          </div>
        )}

        <List
          finished={finished}
          onLoad={onLoad}
          loadingText={
            <Image
              src={`${process.env.IMAGE_DOMAIN}/home/loading.svg`}
              alt="Loading"
              width={36}
              height={36}
            />
          }
          finishedText={productList.length > 0 ? "Finished !" : ""}
          errorText={"Load Error !"}
        >
          {productList.length > 0 ? (
            <div
              className={
                isGridMode
                  ? styles["product-list"]
                  : styles["product-list-full"]
              }
            >
              {productList.map((product: ProductType, index: number) => (
                <div
                  className={
                    isGridMode
                      ? styles["product-item"]
                      : styles["product-item-full"]
                  }
                  key={`${product.productID}-${index}`}
                >
                  {isGridMode ? (
                    <ProductItem product={product} />
                  ) : (
                    <ProductCardFullWidth product={product} />
                  )}
                </div>
              ))}
            </div>
          ) : (
            finished && (
              <div className={styles["empty-list"]}>No item found.</div>
            )
          )}
        </List>
      </div>
    </div>
  );
}
